'use client';

import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';
import { classes } from '../../utils/classes';
import { CmsImageItem } from './CmsImageItem.client';
import type { CmsImageType } from './types';

const styleJustifyContent = (Align?: string | null) => {
  switch (Align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'unset';
  }
};

type CmsImageProps = {
  data: CmsImageType;
};

/**
 * CmsImage component to render image content fetched from the CMS.
 *
 * @param data containing image data and optional fields.
 * @returns JSX.Element A Box component containing the i mage or a linked image.
 */
export const CmsImage = ({
  data,
  className,
  style,
}: CmsImageProps & { className?: string; style?: React.CSSProperties }) => {
  const {
    field_margin: fieldMargin = '0',
    field_distance: distanceType,
    field_alignment: alignment = 'left',
    field_custom_id: dynamicYieldId,
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
    id,
  } = data;

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  return (
    <span
      key={`image-${id}`}
      className={classes('image flex', distanceType === 'variation-1' && 'px-3 lg:px-0', className)}
      {...(dynamicYieldId && { 'data-dy': dynamicYieldId })}
      style={{
        ...(alignment && {
          justifyContent: styleJustifyContent(alignment),
        }),
        ...style,
      }}
    >
      <span
        key={`innerImage-${id}`}
        className="relative flex w-full"
        style={{
          ...(fieldMargin && {
            margin: fieldMargin,
          }),
          ...(alignment && {
            justifyContent: styleJustifyContent(alignment),
          }),
        }}
      >
        {tooltipContent && !!tooltipActivation ? (
          <Tooltip
            content={tooltipContent}
            positioning={tooltipPositioning}
            key={`cmsImageItemTooltip-${data.id}`}
          >
            <CmsImageItem data={data} key={`cmsImageItemWithTooltip-${data.id}`} />
          </Tooltip>
        ) : (
          <CmsImageItem data={data} key={`cmsImageItem-${data.id}`} />
        )}
      </span>
    </span>
  );
};
